import React, { useEffect, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { usePageExit } from "@/lib/pageTransition";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowLeft,
  ArrowRight,
  Gem,
  Palette,
  Brush,
  Type,
  Layers,
  FileDown,
  Handshake,
  CheckCircle2,
  Circle,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const ACCENT = "#E11D2E";
const BG = "#050505";
const MUTED = "#BDBDBD";

const MOCKUP =
  "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/e0ucfvkb_ChatGPT%20Image%2027%20juin%202026%2C%2002_04_48.png";

const LOGO =
  "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/ssol55p1_Logo%20bramsgraphic%20white.png";

const supports = [
  "Site internet",
  "Réseaux sociaux",
  "Cartes de visite",
  "Flyers",
  "Enseignes",
  "Packaging",
  "Vêtements",
  "Véhicules",
  "Documents professionnels",
];

const services = [
  {
    icon: Gem,
    title: "Logo professionnel",
    desc: "Création d'un logo unique, moderne et entièrement personnalisé.",
  },
  {
    icon: Palette,
    title: "Charte graphique",
    desc: "Définition des couleurs, des typographies et des règles d'utilisation de votre identité.",
  },
  {
    icon: Brush,
    title: "Palette de couleurs",
    desc: "Choix de couleurs adaptées à votre activité et à votre image.",
  },
  {
    icon: Type,
    title: "Typographies",
    desc: "Sélection de polices modernes garantissant une identité cohérente.",
  },
  {
    icon: Layers,
    title: "Déclinaisons du logo",
    desc: "Versions couleur, monochrome, blanc, noir et fond transparent.",
  },
  {
    icon: FileDown,
    title: "Fichiers HD",
    desc: "Livraison en PNG, SVG, PDF, AI et autres formats professionnels.",
  },
  {
    icon: Handshake,
    title: "Accompagnement",
    desc: "Nous vous accompagnons jusqu'à la validation finale afin d'obtenir un résultat parfait.",
  },
];

export default function LogosBranding() {
  const rootRef = useRef(null);
  const overlayRef = useRef(null);
  const eyebrowRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const descRef = useRef(null);
  const supportsRef = useRef(null);
  const servicesGridRef = useRef(null);
  const ctaRef = useRef(null);
  const mockupRef = useRef(null);
  const mockupWrapRef = useRef(null);
  const exit = usePageExit();

  // Force theme to dark + scroll to top on mount
  useEffect(() => {
    document.body.classList.add("dark");
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // GSAP entrance + ScrollTrigger
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Page fade-in overlay
      gsap.to(overlayRef.current, {
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        onComplete: () => {
          if (overlayRef.current) overlayRef.current.style.pointerEvents = "none";
        },
      });

      const textTargets = [
        eyebrowRef.current,
        titleRef.current,
        subtitleRef.current,
        descRef.current,
        supportsRef.current,
        servicesGridRef.current,
        ctaRef.current,
      ].filter(Boolean);

      gsap.set(textTargets, { opacity: 0, x: -80 });
      gsap.set(mockupWrapRef.current, { opacity: 0, x: 120, scale: 0.92, rotation: 2 });

      // Master timeline driven by entry
      const tl = gsap.timeline({ delay: 0.15 });
      tl.to(textTargets, {
        opacity: 1,
        x: 0,
        duration: 0.8,
        ease: "power3.out",
        stagger: 0.15,
      });
      tl.to(
        mockupWrapRef.current,
        {
          opacity: 1,
          x: 0,
          scale: 1,
          rotation: 0,
          duration: 1,
          ease: "power4.out",
        },
        "-=0.9"
      );

      // Infinite float on mockup
      gsap.to(mockupRef.current, {
        y: -8,
        duration: 5,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      // ScrollTrigger reveals for sections further down (services grid, supports)
      ScrollTrigger.batch(".lb-reveal", {
        start: "top 85%",
        once: true,
        onEnter: (els) =>
          gsap.fromTo(
            els,
            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", stagger: 0.08 }
          ),
      });
    }, rootRef);

    return () => ctx.revert();
  }, []);

  // Mockup hover
  const handleMockupEnter = () => {
    gsap.to(mockupRef.current, {
      scale: 1.03,
      rotationX: 2,
      rotationY: -3,
      duration: 0.5,
      ease: "power3.out",
      transformPerspective: 1000,
    });
  };
  const handleMockupLeave = () => {
    gsap.to(mockupRef.current, {
      scale: 1,
      rotationX: 0,
      rotationY: 0,
      duration: 0.5,
      ease: "power3.out",
    });
  };

  return (
    <div
      ref={rootRef}
      data-testid="logos-branding-page"
      className="relative min-h-screen text-white overflow-x-hidden"
      style={{ backgroundColor: BG, fontFamily: "'Geist Sans','Inter',system-ui,sans-serif" }}
    >
      {/* Entry overlay */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[60]"
        style={{ backgroundColor: BG, opacity: 1 }}
        aria-hidden="true"
      />

      {/* Subtle red glow ambient background */}
      <div
        className="pointer-events-none fixed inset-0"
        aria-hidden="true"
        style={{
          background: `radial-gradient(60% 40% at 50% 0%, ${ACCENT}10 0%, transparent 60%),
                       radial-gradient(50% 35% at 100% 100%, ${ACCENT}0a 0%, transparent 60%)`,
          zIndex: 0,
        }}
      />

      {/* Mini top nav */}
      <header className="relative z-20 max-w-7xl mx-auto px-6 lg:px-10 pt-7 md:pt-10 flex items-center justify-between">
        <Link
          to="/"
          onClick={(e) => { e.preventDefault(); exit("realisations"); }}
          data-testid="lb-back-home"
          className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] opacity-75 hover:opacity-100 transition-all"
        >
          <span className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center transition-all group-hover:-translate-x-1 group-hover:border-white/50">
            <ArrowLeft size={14} />
          </span>
          Retour
        </Link>
        <Link to="/" className="flex items-center" data-testid="lb-logo">
          <img src={LOGO} alt="Brams Graphic Design" className="h-12 md:h-14 object-contain" />
        </Link>
        <div className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] opacity-60">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: ACCENT }}
          />
          Service · 01
        </div>
      </header>

      {/* Hero / Two-column section */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div
              ref={eyebrowRef}
              className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em]"
              style={{ color: MUTED }}
            >
              <span className="h-px w-8" style={{ background: ACCENT }} />
              Logos & Branding
            </div>

            <h1
              ref={titleRef}
              data-testid="lb-title"
              className="mt-6 font-display font-bold tracking-tighter leading-[1.02] text-5xl sm:text-6xl lg:text-7xl"
              style={{ letterSpacing: "-0.04em" }}
            >
              Logos &amp; <span style={{ color: ACCENT }}>Branding</span>
            </h1>

            <p
              ref={subtitleRef}
              data-testid="lb-subtitle"
              className="mt-6 text-lg md:text-xl font-light"
              style={{ color: MUTED }}
            >
              Une identité visuelle pensée pour faire grandir votre marque.
            </p>

            <div ref={descRef} data-testid="lb-description" className="mt-10 space-y-5 max-w-2xl">
              <p className="text-base md:text-[17px] leading-relaxed font-light text-white/85">
                Votre identité visuelle est bien plus qu&apos;un simple logo : c&apos;est la première
                impression que vos clients auront de votre entreprise.
              </p>
              <p className="text-base md:text-[17px] leading-relaxed font-light text-white/80">
                Chez <strong className="font-medium">Brams Graphic</strong>, nous concevons des
                identités visuelles modernes, élégantes et cohérentes afin de renforcer votre image
                de marque et de vous démarquer durablement de la concurrence.
              </p>
              <p className="text-base md:text-[17px] leading-relaxed font-light text-white/80">
                Chaque projet est entièrement réalisé sur mesure en fonction de votre activité, de
                vos valeurs et de votre cible. Notre objectif est de créer une identité forte,
                professionnelle et mémorable qui inspire immédiatement confiance.
              </p>
              <p
                className="text-sm uppercase tracking-[0.3em] pt-2"
                style={{ color: MUTED }}
              >
                Utilisable sur tous vos supports :
              </p>

              {/* Supports list */}
              <ul
                ref={supportsRef}
                data-testid="lb-supports"
                className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 pt-2"
              >
                {supports.map((s) => (
                  <li
                    key={s}
                    className="flex items-center gap-3 text-[15px] font-light text-white/85"
                  >
                    <span
                      className="inline-flex w-6 h-6 items-center justify-center rounded-full"
                      style={{
                        background: "rgba(225,29,46,0.08)",
                        border: `1px solid ${ACCENT}33`,
                      }}
                    >
                      <CheckCircle2 size={12} style={{ color: ACCENT }} />
                    </span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>

            {/* Services Grid */}
            <div className="mt-16">
              <div className="flex items-end justify-between gap-6 mb-8">
                <h2 className="font-display font-semibold text-2xl md:text-3xl tracking-tight">
                  Ce que comprend la prestation
                </h2>
                <span
                  className="hidden md:inline-block text-[10px] uppercase tracking-[0.3em]"
                  style={{ color: MUTED }}
                >
                  07 livrables
                </span>
              </div>

              <div
                ref={servicesGridRef}
                data-testid="lb-services-grid"
                className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4"
              >
                {services.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div
                      key={s.title}
                      data-testid={`lb-service-${i}`}
                      className="lb-reveal group relative p-6 transition-all duration-500 ease-out"
                      style={{
                        background: "#0B0B0B",
                        border: "1px solid rgba(255,255,255,0.06)",
                        borderRadius: 18,
                      }}
                    >
                      <span
                        className="absolute left-0 top-0 h-[2px] w-0 group-hover:w-full transition-all duration-700"
                        style={{ background: ACCENT }}
                      />
                      <div className="flex items-start gap-4">
                        <div
                          className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110"
                          style={{
                            background: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(255,255,255,0.08)",
                          }}
                        >
                          <Icon size={18} strokeWidth={1.5} style={{ color: ACCENT }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-display font-semibold tracking-tight text-base md:text-lg">
                            {s.title}
                          </h3>
                          <p className="mt-2 text-sm leading-relaxed font-light" style={{ color: MUTED }}>
                            {s.desc}
                          </p>
                        </div>
                        <Circle
                          size={10}
                          strokeWidth={1.2}
                          className="opacity-30 group-hover:opacity-100 transition-opacity"
                          style={{ color: ACCENT }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA */}
            <div ref={ctaRef} className="mt-14">
              <Link
                to="/#contact"
                data-testid="lb-cta-quote"
                className="group relative inline-flex items-center gap-3 rounded-full pl-8 pr-3 py-3.5 font-medium text-sm tracking-wide transition-all duration-300 ease-out hover:scale-[1.04] active:scale-95"
                style={{
                  background: ACCENT,
                  color: "#fff",
                  boxShadow: `0 10px 40px -10px ${ACCENT}99`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 18px 60px -10px ${ACCENT}cc, 0 0 0 6px rgba(225,29,46,0.12)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = `0 10px 40px -10px ${ACCENT}99`;
                }}
              >
                <span className="uppercase tracking-[0.25em]">Demander un devis</span>
                <span
                  className="flex items-center justify-center w-10 h-10 rounded-full transition-transform duration-500 group-hover:translate-x-1"
                  style={{ background: "rgba(0,0,0,0.25)" }}
                >
                  <ArrowRight size={16} />
                </span>
              </Link>
              <p className="mt-4 text-xs uppercase tracking-[0.3em]" style={{ color: MUTED }}>
                Réponse personnalisée sous 48 h.
              </p>
            </div>
          </div>

          {/* RIGHT COLUMN — MOCKUP */}
          <div className="lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-10">
            <div
              ref={mockupWrapRef}
              className="relative"
              style={{ perspective: 1200 }}
            >
              {/* Soft red glow halo */}
              <div
                className="absolute -inset-10 rounded-[40px] pointer-events-none"
                style={{
                  background: `radial-gradient(closest-side, ${ACCENT}26, transparent 70%)`,
                  filter: "blur(40px)",
                }}
                aria-hidden="true"
              />

              <div
                ref={mockupRef}
                onMouseEnter={handleMockupEnter}
                onMouseLeave={handleMockupLeave}
                className="relative overflow-hidden"
                data-testid="lb-mockup"
                style={{
                  borderRadius: 24,
                  background: "#0A0A0A",
                  border: "1px solid rgba(255,255,255,0.06)",
                  boxShadow:
                    "0 30px 80px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04)",
                  transformStyle: "preserve-3d",
                  willChange: "transform",
                }}
              >
                <img
                  src={MOCKUP}
                  alt="Logos & Branding — Identité visuelle BSG"
                  className="block w-full h-auto select-none"
                  draggable={false}
                  loading="eager"
                />
                {/* Top reflection */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 25%)",
                  }}
                />
                {/* Bottom subtle scrim */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 70%, rgba(0,0,0,0.45) 100%)",
                  }}
                />
              </div>

              {/* Floor reflection */}
              <div
                aria-hidden="true"
                className="mt-3 mx-8 h-10 opacity-50"
                style={{
                  background:
                    "radial-gradient(ellipse at center, rgba(225,29,46,0.18) 0%, transparent 70%)",
                  filter: "blur(10px)",
                }}
              />

              {/* Side badges */}
              <div
                className="lb-reveal mt-8 grid grid-cols-3 gap-3"
              >
                {[
                  { k: "Sur-mesure", v: "100%" },
                  { k: "Révisions", v: "Illimitées" },
                  { k: "Livraison", v: "HD · multi-formats" },
                ].map((b) => (
                  <div
                    key={b.k}
                    className="p-4 text-center"
                    style={{
                      background: "#0B0B0B",
                      border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: 18,
                    }}
                  >
                    <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: MUTED }}>
                      {b.k}
                    </div>
                    <div className="mt-1 font-display font-semibold text-sm tracking-tight">
                      {b.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA strip */}
      <section
        className="lb-reveal relative z-10 border-t border-white/[0.06] py-16 md:py-20"
        style={{ background: "#070707" }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <span
              className="text-[10px] uppercase tracking-[0.4em]"
              style={{ color: MUTED }}
            >
              Prêt à démarrer ?
            </span>
            <h3 className="mt-3 font-display font-semibold text-3xl md:text-4xl tracking-tighter">
              Donnons vie à votre <span style={{ color: ACCENT }}>identité</span>.
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/#contact"
              onClick={(e) => { e.preventDefault(); exit("contact"); }}
              data-testid="lb-cta-bottom-contact"
              className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-medium text-sm transition-all duration-300 hover:scale-[1.03] active:scale-95"
              style={{ background: "#fff", color: "#000" }}
            >
              Me contacter
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/"
              onClick={(e) => { e.preventDefault(); exit("realisations"); }}
              data-testid="lb-cta-bottom-home"
              className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-medium text-sm transition-all duration-300 hover:scale-[1.03] active:scale-95 border border-white/20 hover:bg-white/5"
            >
              Voir d&apos;autres services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

